import React from 'react'
import debounce from 'lodash.debounce'

import {
    addListener,
    removeListener,
    ScrollEventListener,
} from 'component-library/__helpers/global-dom-events'
import {
    SlotRenderEndedEvent,
    GptManager,
} from 'component-library/advertising/gpt/GptAdProvider/components/GptManager'
import { GptAdSlotDefinition } from 'common/advertising/gpt-ad-slot-definintion'
import { adBlockEnabled } from 'component-library/advertising/gpt/GptAdProvider/utils'
import { GptAdProviderContext } from './GptAdProviderContext'

let lazyLoadingAlgorithm = (velocity: number) => 300 + Math.abs(velocity)

/**
 * Change the lazy loading algorithm
 * Format: (scrolling velocity in px/s) => max distance to load ads
 * A negative distance can be used to not load the slot at all
 */
export const setLazyLoadingAlgorithm = (algorithm: (velocity: number) => number) => {
    lazyLoadingAlgorithm = algorithm
}

export interface AdProviderOptions {
    /**
     * Use the renderKey to tell the GptAdProvider when to update its ads.
     * If not used, it falls back to a deep equality check with worse performance
     */
    renderKey?: string

    /**
     * Updates the correlator when ad definitions change by default, set to false to disable
     * Google's servers maintain the Correlator (~30 seconds) so this isn't guaranteed depending on timing
     * https://developers.google.com/doubleclick-gpt/reference#googletag.PubAdsService_refresh
     */
    updateCorrelator?: boolean

    /** Disable lazy loading and load all slots at once */
    disableLazyLoading?: boolean

    /** Wait for all defined slots to be mounted before loading any ads */
    waitForSlotsToMount?: boolean
}

export interface GptAdSlotRegistration {
    id: string
    disabled: boolean
    onSlotRenderEnded: (event: googletag.events.SlotRenderEndedEvent) => void
    getViewportPosition: () => number | undefined
}

export interface Props extends AdProviderOptions {
    gptManager: GptManager
    slots: GptAdSlotDefinition[]
}

export interface ChildContext {
    gptAdProvider: GptAdProvider
}

let gptEnabled = true

export const setGptEnabled = (val: boolean) => {
    gptEnabled = val
}

export const getGptEnabled = () => {
    return gptEnabled
}

export class GptAdProvider extends React.Component<Props, {}> {
    static displayName = 'GptAdProvider'

    static defaultProps: Partial<Props> = {
        updateCorrelator: true,
    }

    private slots: GptAdSlotRegistration[] = []
    private active = !adBlockEnabled() && gptEnabled
    private displayedSlots: string[] = []
    private shouldUpdateAds = false
    private shouldUpdateCorrelator = false
    private scrollVelocity = 0 // px/s
    private eventListenersActive = false

    async componentWillMount() {
        if (this.active) {
            await this.props.gptManager.ensureInitialised()
            this.props.gptManager.registerOnSlotRenderEnded(this.onSlotRenderEnded.bind(this))
            this.updateEventListeners()
            this.shouldUpdateCorrelator = true
        }
    }

    componentWillUnmount() {
        if (this.active) {
            // async invocation of `destroySlots` should be ok here
            this.props.gptManager.destroySlots(this.props.slots).catch(err => {
                console.warn(
                    'GptAdProvider: Error occured during invocation of destroySlots()',
                    err
                )
            })
            this.removeEventListeners()
            this.slots = []
            this.displayedSlots = []
        }
    }

    componentWillUpdate(nextProps: Props) {
        if (!this.active) {
            return
        }

        this.shouldUpdateAds = false

        if (this.props.renderKey || nextProps.renderKey) {
            this.shouldUpdateAds = this.props.renderKey !== nextProps.renderKey
        } else if (nextProps.slots !== this.props.slots) {
            console.warn('GptAdProvider: no renderKey provided, falling back to slot hashing!')
            this.shouldUpdateAds = haveSlotsChanged(this.props.slots, nextProps.slots)
        }

        if (this.shouldUpdateAds) {
            /**
             * `destroySlots` is async but we need not `await` it, as React does not wait for async
             * lifecycle methods. i.e., `componentDidUpdate` will be invoked even if this promise
             * has not completed. Furthermore; `shouldDisplaySlot` depends on `displayedSlots` array
             * to not contain the same gpt slot ID as one that is being added during a new page load.
             */
            this.props.gptManager.destroySlots(this.props.slots).catch(err => {
                console.warn(
                    'GptAdProvider: Error occured during invocation of destroySlots()',
                    err
                )
            })
            this.displayedSlots = []
            this.cleanUpSlots()
        }
    }

    componentDidUpdate() {
        if (this.active && this.shouldUpdateAds) {
            this.shouldUpdateCorrelator = true
            this.loadWhenIdle()
            this.updateEventListeners()
        }
    }

    render() {
        return (
            <GptAdProviderContext.Provider value={this}>
                {React.Children.only(this.props.children)}
            </GptAdProviderContext.Provider>
        )
    }

    registerSlot(slot: GptAdSlotRegistration) {
        if (!this.active) {
            return
        }

        const id = slot.id

        const present = !!this.props.slots.filter(currentSlot => currentSlot.id === id).length

        if (!present) {
            console.error('GPT slot definition not present in GptAdProvider!')
            console.error('Slot:', slot)
            console.error('Slot definitions:', this.props.slots)
            return
        }

        const registered = this.slots.filter(currentSlot => currentSlot.id === id)

        if (registered.length) {
            console.error('GPT slot with the same ID registered multiple times!')
            console.error('Registered slot:', registered[0])
            console.error('New slot:', slot)
            return
        }

        this.slots.push(slot)

        if (this.props.waitForSlotsToMount) {
            if (this.slots.length === this.props.slots.length) {
                this.loadWhenIdle()
            }
        } else {
            this.loadDebounced()
        }
    }

    unregisterSlot(slot: GptAdSlotRegistration) {
        if (!this.active) {
            return
        }

        const id = slot.id
        const slotIndex = this.slots.findIndex(slotRefference => slotRefference.id === slot.id)

        if (slotIndex !== -1) {
            this.slots.splice(slotIndex, 1)

            const slotDef = this.mapSlotToDefinition(slot)
            const loadedIndex = this.displayedSlots.indexOf(id)

            if (slotDef && loadedIndex !== -1) {
                this.props.gptManager.destroySlots([slotDef]).catch(err => {
                    console.warn(
                        'GptAdProvider: Error occured during invocation of destroySlots()',
                        err
                    )
                })
                this.displayedSlots.splice(loadedIndex, 1)
            }
        }
    }

    onSlotRenderEnded(event: SlotRenderEndedEvent) {
        const slotId = event.slot.getSlotElementId()
        this.slots.filter(slot => slot.id === slotId).forEach(slot => slot.onSlotRenderEnded(event))
    }

    handleResize = () => {
        this.scrollVelocity = 0
        this.loadWhenIdle()
    }

    handleScroll: ScrollEventListener = ({ velocity }) => {
        this.scrollVelocity = velocity
        this.loadWhenIdle()
    }

    // we debounce loading when triggered by a slot mounting
    // so that we send fewer requests to GPT
    loadDebounced = debounce(() => this.loadWhenIdle(), 100)

    // otherwise we try to load when the browser is idle (within a timeout)
    loadWhenIdle = () => {
        if (this.hasLoadedAll()) {
            return
        }
        window.requestIdleCallback(() => this.displayViewableSlots(), { timeout: 100 })
    }

    private shouldDisplaySlot = (slot: GptAdSlotRegistration) => {
        const { id, disabled } = slot
        const alreadyLoaded = this.displayedSlots.indexOf(id) !== -1
        const definition = this.mapSlotToDefinition(slot)

        if (disabled || alreadyLoaded || !definition) {
            return false
        }

        const disableLazyLoading =
            this.props.disableLazyLoading || definition.disableLazyLoading || definition.outOfPage

        if (disableLazyLoading) {
            return true
        }

        const viewportPosition = slot.getViewportPosition()

        if (viewportPosition === undefined) {
            // not visible
            return false
        }

        const slotDistance = Math.abs(viewportPosition)
        const loadDistance =
            definition.lazyLoadingDistance !== undefined
                ? definition.lazyLoadingDistance
                : lazyLoadingAlgorithm(this.scrollVelocity)

        // if loadDistance < 0, loading ads can be disabled completely
        // this can be used to disable loading ads when scrolling very fast
        const withinDistance = slotDistance <= loadDistance

        if (!withinDistance) {
            return false
        }

        // only load ads in scroll direction
        const shouldDisplay =
            this.scrollVelocity >= 0 ? viewportPosition >= 0 : viewportPosition <= 0

        return shouldDisplay
    }

    private mapSlotToDefinition = (slot: GptAdSlotRegistration) =>
        this.props.slots.find(slotDefinition => slotDefinition.id === slot.id)

    private shouldLoad() {
        const { disableLazyLoading, waitForSlotsToMount } = this.props
        // document.hidden is always true in the tests
        const tabActive = !document.hidden || process.env.NODE_ENV === 'test'
        const allSlotsMounted = this.slots.length === this.props.slots.length

        /**
         * we do NOT load when
         * - the tab is inactive
         * - we are still waiting for all slots to mount (if set)
         * - all slots have been loaded already
         */
        const shouldLoad =
            (tabActive || disableLazyLoading) &&
            (!waitForSlotsToMount || allSlotsMounted) &&
            !this.hasLoadedAll()

        return shouldLoad
    }

    private displayViewableSlots() {
        if (!this.shouldLoad()) {
            return
        }

        const slotsToLoad: GptAdSlotDefinition[] = this.slots
            .filter(slot => {
                const shouldDisplay = this.shouldDisplaySlot(slot)
                return shouldDisplay
            })
            .map(this.mapSlotToDefinition)
            .filter((slot): slot is GptAdSlotDefinition => slot !== undefined)

        if (slotsToLoad.length === 0) {
            return
        }

        slotsToLoad.forEach(slot => {
            if (this.displayedSlots.indexOf(slot.id) === -1) {
                this.displayedSlots.push(slot.id)
            }
        })

        this.props.gptManager.displaySlots(
            slotsToLoad,
            this.props.updateCorrelator && this.shouldUpdateCorrelator
        )
        // We only want to update the correlator on page change
        if (this.shouldUpdateCorrelator) {
            this.shouldUpdateCorrelator = false
        }

        this.updateEventListeners()
    }

    private cleanUpSlots() {
        const ids = this.props.slots.map(slot => slot.id)
        this.slots = this.slots.filter(slot => ids.indexOf(slot.id) !== -1)
        this.displayedSlots = this.displayedSlots.filter(id => ids.indexOf(id) !== -1)
    }

    private hasLoadedAll() {
        return this.props.slots.length === this.displayedSlots.length
    }

    private updateEventListeners() {
        if (this.props.disableLazyLoading || this.hasLoadedAll()) {
            this.removeEventListeners()
            return
        }

        if (this.eventListenersActive) {
            return
        }

        addListener('scroll', this.handleScroll)
        addListener('resize', this.handleResize)
        addListener('visibility', this.loadWhenIdle)
        this.eventListenersActive = true
    }

    private removeEventListeners() {
        if (!this.eventListenersActive) {
            return
        }

        removeListener('scroll', this.handleScroll)
        removeListener('resize', this.handleResize)
        removeListener('visibility', this.loadWhenIdle)
        this.eventListenersActive = false
    }
}

/**
 * Naïve function that hashes slots using `JSON.stringify`
 * and evaluates whether the slots have changed using a literal string comparison.
 */
export function haveSlotsChanged(
    currentSlots: GptAdSlotDefinition[],
    nextSlots: GptAdSlotDefinition[]
) {
    try {
        const currentSlotsHash = JSON.stringify(currentSlots)
        const nextSlotsHash = JSON.stringify(nextSlots)
        return currentSlotsHash !== nextSlotsHash
    } catch (err) {
        console.error(`GptAdProvider: slot comparison failed! ${err}`)
        return true
    }
}
